/* eslint-disable no-param-reassign */

'use client';

import axios, { type AxiosInstance } from 'axios';
import { getCookie, deleteCookie } from 'cookies-next';
import { isPublicAuthPath, shouldEndSession } from './session-expiry';
import { denyAccess, refusalFromError } from '../permissions/access-denial';

/**
 * Backend host. Override per environment with NEXT_PUBLIC_API_HOST
 * (e.g. `http://localhost:8000` in `.env.local` for a local backend).
 * Inlined at build time by Next.js, so restart the dev server after changing it.
 */
const HOST = (
  process.env.NEXT_PUBLIC_API_HOST || 'https://backend.globallinkplus.com'
).replace(/\/+$/, '');

export const baseURL = `${HOST}/api/`;

/**
 * Interceptors run outside React rendering, so Next.js navigation hooks
 * (usePathname/redirect) are off-limits here — use the browser location.
 */
function redirectToSignIn(): void {
  if (typeof window === 'undefined') return;
  // Requests can fire from the sign-in page itself (e.g. the root-layout
  // ThemeInitializer customization query). Redirecting again from there would
  // wrap the current /sign-in?redirectUrl=… into a new redirectUrl on every
  // page load — an infinite redirect loop with ever-nesting encoding.
  if (isPublicAuthPath(window.location.pathname)) return;
  const currentUrl = window.location.pathname + window.location.search;
  window.location.assign(
    `/sign-in?redirectUrl=${encodeURIComponent(currentUrl)}`
  );
}

function attachInterceptors(instance: AxiosInstance): AxiosInstance {
  instance.interceptors.request.use(async (config) => {
    const token = getCookie('token');

    if (!token) {
      redirectToSignIn();
    }

    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // A portal action the backend refused names the tags it needs. That
      // becomes a request the rep can send, over the page they are on, the
      // way the main app does it. The call still rejects, so the screen
      // that made it reports the failure as before.
      const refusal = refusalFromError(error);
      if (refusal && typeof window !== 'undefined') {
        denyAccess({
          kind: 'action',
          permissions: refusal.permissions,
          page: window.location.pathname,
          message: refusal.message
        });
      }

      if (error.response) {
        if (
          shouldEndSession(error.response.status, originalRequest?.url) &&
          !originalRequest?.retry
        ) {
          deleteCookie('token');
          deleteCookie('userId');
          if (typeof window !== 'undefined') {
            localStorage.removeItem('user');
          }

          // Redirect to login shortly after so the session-expired state is visible
          setTimeout(() => {
            redirectToSignIn();
          }, 1200);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
}

/**
 * Default axios instance for sales-portal app code.
 * baseURL ends with `/api/` — call sites omit the `/api/` prefix
 * (e.g. `axiosClient.get('/users')`).
 */
const axiosClient = attachInterceptors(
  axios.create({
    baseURL,
    timeout: 500000
  })
);

/**
 * Sibling instance with baseURL = host root (no `/api/`).
 * Use this for libraries whose endpoint strings already include `/api/...`,
 * e.g. `@glperp/catalog`. Auth interceptors are identical to `axiosClient`.
 */
export const apiHostClient = attachInterceptors(
  axios.create({
    baseURL: `${HOST}/`,
    timeout: 500000
  })
);

export default axiosClient;
